import type { ApiResponse } from '@container-system/shared';

/**
 * Gegenstück zum Backend-CSRF-Check (`backend/src/middleware/csrf.ts`): jeder
 * zustandsändernde Request setzt diesen Custom-Header. Ein von einer fremden
 * Seite ausgelöster Request (Formular, <img>, ...) kann das nicht.
 */
const CSRF_HEADER = 'x-container-system-client';

export class ApiRequestError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

let refreshPromise: Promise<void> | null = null;

async function refreshSession(): Promise<void> {
  refreshPromise ??= fetch('/api/auth/refresh', { method: 'POST', credentials: 'include', headers: { [CSRF_HEADER]: '1' } })
    .then((res) => {
      if (!res.ok) throw new Error('refresh-failed');
    })
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
}

/**
 * Zentrale Fetch-Hülle: setzt den CSRF-Header bei mutierenden Requests,
 * schickt Cookies mit (`credentials: 'include'`) und versucht bei einem
 * abgelaufenen Access-Token GENAU EINMAL automatisch einen Refresh, bevor der
 * ursprüngliche Request wiederholt wird - für den Nutzer unsichtbar, solange
 * die Sitzung (Refresh-Token) noch gültig ist.
 */
async function fetchWithAuth(path: string, options: RequestInit, retry = true): Promise<Response> {
  const method = (options.method ?? 'GET').toUpperCase();
  const isMutating = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);

  const headers = new Headers(options.headers);
  if (isMutating) headers.set(CSRF_HEADER, '1');
  if (options.body && !(options.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const res = await fetch(path, { ...options, method, headers, credentials: 'include' });

  // Nur /login und /refresh selbst dürfen KEINEN Refresh-Retry auslösen (sonst
  // Endlosschleife bzw. ein 401 wegen Falschpasswort würde einen Refresh-Versuch
  // provozieren). Alle anderen /api/auth/*-Aufrufe - allen voran /api/auth/me,
  // der Sitzungs-Check beim App-Start - MÜSSEN den Retry durchlaufen, sonst wird
  // ein Nutzer mit abgelaufenem (15min) Access-Token aber noch gültigem
  // Refresh-Token (bis zu 30 Tage) fälschlich als ausgeloggt behandelt.
  const skipRefresh = path === '/api/auth/login' || path === '/api/auth/refresh';
  if (res.status === 401 && retry && !skipRefresh) {
    try {
      await refreshSession();
      return fetchWithAuth(path, options, false);
    } catch {
      // WICHTIG: `/api/auth/me` ist der Sitzungs-Check beim App-Start bzw. auf
      // der Login-Seite selbst - ein gescheiterter Refresh bedeutet dort meist
      // schlicht "noch nie angemeldet", nicht "Sitzung mitten in der Nutzung
      // abgelaufen". Ein harter `window.location.assign('/login')` hätte hier
      // auf der Login-Seite eine Reload-Schleife ausgelöst (Seite lädt -> me()
      // 401 -> Refresh scheitert mangels Cookie -> Reload -> von vorn). Der
      // Aufrufer (AuthContext) fängt das bereits sauber als "nicht angemeldet"
      // ab. Für alle anderen authentifizierten Aufrufe bleibt der harte
      // Redirect als Fallback für eine wirklich mitten in der Nutzung
      // abgelaufene Sitzung bestehen.
      if (path !== '/api/auth/me') {
        window.location.assign('/login');
      }
      throw new ApiRequestError('UNAUTHORIZED', 'Sitzung abgelaufen, bitte erneut anmelden.', 401);
    }
  }

  return res;
}

export async function apiRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetchWithAuth(path, options);
  if (res.status === 204) return undefined as T;

  const body = (await res.json()) as ApiResponse<T>;
  if (!body.success) throw new ApiRequestError(body.error.code, body.error.message, res.status, body.error.details);
  return body.data;
}

/** Für Datei-Downloads (Export, Dokumente) - der Server liefert hier keine JSON-Hülle. */
export async function apiDownload(path: string): Promise<Blob> {
  const res = await fetchWithAuth(path, {});
  if (!res.ok) throw new ApiRequestError('DOWNLOAD_FAILED', 'Download fehlgeschlagen.', res.status);
  return res.blob();
}

/**
 * Nimmt bewusst `object` statt `Record<string, ...>` als Parametertyp - eine
 * konkrete Filter-Interface (z.B. `ExportFilter`) ohne eigene Indexsignatur
 * ist zu `Record<string, V>` nicht direkt zuweisbar (TypeScript-Eigenheit),
 * obwohl strukturell alles passt.
 */
export function buildQuery(params: object): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params as Record<string, string | number | boolean | undefined | null>)) {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}
